import { useEffect, useState } from "react";
import { Mail } from "lucide-react";
import SEO from "../components/SEO";
import ChapterHero from "../components/ChapterHero";
import NextPage from "../components/NextPage";
import CedarSign, { CedarSignText } from "../components/CedarSign";
import { getOurCowmunity } from "../lib/api";

const WINK_SRC = "/saltie-wink.png";

function Prose({ text, className = "" }) {
  const parts = (text || "").split("\n\n").filter(Boolean);
  return (
    <div className={className}>
      {parts.map((p, i) => (
        <p key={i} className="whitespace-pre-line mb-6 last:mb-0">
          {p}
        </p>
      ))}
    </div>
  );
}

function SaltieWink({ style }) {
  return (
    <img
      src={WINK_SRC}
      alt=""
      aria-hidden="true"
      draggable="false"
      className="pointer-events-none select-none absolute"
      style={{ width: "clamp(3rem, 5vw, 4rem)", opacity: 0.5, ...style }}
    />
  );
}

/**
 * "Caught Something Moo-velous?" — its own chapter for community photo
 * submissions. Backed by the same `our_cow_munity` singleton fields
 * (moovelous_*, pasture_photo_*) so nothing new to edit in Admin.
 */
export default function MooVelous() {
  const [c, setC] = useState(null);

  useEffect(() => {
    getOurCowmunity().then(setC).catch(() => setC({}));
  }, []);

  if (!c) {
    return (
      <div className="parchment min-h-screen flex items-center justify-center">
        <p className="text-sm text-[#6D5A50] italic">{"loading\u2026"}</p>
      </div>
    );
  }

  return (
    <div className="parchment min-h-screen" data-testid="moo-velous-page">
      <SEO
        title={c.moovelous_seo_title || "Caught Something Moo-velous?"}
        description={c.moovelous_seo_description || "Send us a photo, a farm logo, or a favorite Moo-moment from around our Cow-munity. Every now and then we freshen this chapter with a few favorites."}
        path="/moo-velous"
      />

      <ChapterHero
        path="/moo-velous"
        image="/assets/heroes/moo-velous.webp"
        title={c.moovelous_heading || "Caught Something Moo-velous?"}
        subtitle={c.moovelous_hero_subtitle || "a little scrapbook for our Cow-munity"}
        position="top-left"
      />

      <div className="max-w-4xl mx-auto px-6 -mt-8 relative z-10 pb-28">
        {/* Hero introduction */}
        <section data-testid="moo-intro" className="relative pt-16 text-center">
          <SaltieWink style={{ top: "-1rem", right: "5%", transform: "rotate(14deg) scaleX(-1)" }} />
          <div className="flex justify-center">
            <CedarSign size="lg">
              <CedarSignText size="text-2xl md:text-4xl">
                {(() => {
                  const t = c.moovelous_heading || "Caught Something Moo-velous?";
                  const mv = t.indexOf("Moo-velous");
                  if (mv > 0) {
                    return (
                      <span className="block text-center leading-tight px-3 py-2">
                        <span className="block">{t.slice(0, mv).trim()}</span>
                        <span
                          className="block whitespace-nowrap"
                          style={{ fontSize: "1.3em", marginTop: "0.15em" }}
                        >
                          {t.slice(mv)}
                        </span>
                      </span>
                    );
                  }
                  return (
                    <span className="block text-center leading-tight px-3 py-2">
                      {t}
                    </span>
                  );
                })()}
              </CedarSignText>
            </CedarSign>
          </div>

          <div className="mt-14 max-w-3xl mx-auto text-left md:text-center">
            <Prose
              text={c.moovelous_body}
              className="font-body text-xl md:text-2xl leading-loose text-[#2C2724]/90"
            />
          </div>

          {/* Highlighted email CTA */}
          {c.moovelous_email && (
            <div className="mt-10 flex justify-center">
              <a
                href={`mailto:${c.moovelous_email}`}
                data-testid="moo-email-btn"
                className="btn-pasture inline-flex items-center gap-2 text-lg"
                style={{ padding: "0.9rem 2rem 1rem" }}
              >
                <Mail size={18} />
                {c.moovelous_email}
              </a>
            </div>
          )}
        </section>

        {/* Community Photo Gallery — four editable placeholders */}
        <section
          data-testid="moo-gallery"
          className="relative mt-24"
        >
          <SaltieWink style={{ top: "-1rem", left: "6%" }} />
          <p className="text-center font-script text-3xl md:text-4xl text-[#4B3A2F] mb-10">
            {c.pasture_body || "A few moments shared by our Cow-munity."}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[1, 2, 3, 4].map((n) => {
              const url = c[`pasture_photo_${n}`];
              return (
                <div
                  key={n}
                  data-testid={`moo-photo-${n}`}
                  className="relative aspect-square bg-[#EFE4CE] border border-[#E7DFD5] rounded-md overflow-hidden page-shadow"
                >
                  {url ? (
                    <img
                      src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL || ""}${url}`}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                      draggable="false"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-[#8D6E63] font-script text-lg md:text-xl px-4 text-center">
                      A little pasture moment
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="font-script text-2xl md:text-3xl text-[#4B3A2F] mt-14 text-center">
            {c.moovelous_closing_line || "Because the best stories \u2014 and the best smiles \u2014 are meant to be shared."}
          </p>
        </section>
      </div>

      <NextPage to="/meet-the-family" label={c.moovelous_next_button_label || "Meet the Family"} />
    </div>
  );
}
